import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableData } from '@anytag/shared-types';
import { formatIntNumber } from '@anytag/shared-utils';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { Anchor, TableCell } from '@anytag/shared-atoms';
import {
  SocialAccountType,
  InstagramUserHashtagPostsSortOrder,
  InstagramUserHashtagListPostsItemPayload
} from '@anytag/graphql';
import { PostCell } from '../../../shared';

type Props = Pick<TableData<InstagramUserHashtagListPostsItemPayload>, 'data'>;

export const useInstagramHashtagsAnalyticsPostsListTable = ({ data = [] }: Props) => {
  const { t } = useTranslation();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();

  const columns: ColumnDef<InstagramUserHashtagListPostsItemPayload>[] = [
    {
      accessorKey: 'rank',
      header: t<string>('HeaderColumn.Rank'),
      meta: { align: 'center', sortField: InstagramUserHashtagPostsSortOrder.RANK },
      cell: ({ row }) => <div className="pr-2">{formatIntNumber(row.original.rank + 1)}</div>
    },
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencerId, accountId, avatar, name, followersCount } = row.original;
        const profileLink = generateProfileLink(influencerId, {
          filter: { id: String(accountId), sm: SocialAccountType.INSTAGRAM }
        });

        return (
          <div className="flex w-50 items-center gap-2">
            <InfluencerAvatar {...profileLink} size="40px" src={avatar} className="rounded-none" />
            <div>
              <Anchor {...profileLink} label={name || '-'} className="text-base" />
              <div className="mt-1 text-xxs">
                {t('Follower')}:&nbsp;{formatIntNumber(followersCount)}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'post',
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => {
        const { postId, content, media } = row.original;

        return (
          <PostCell
            postId={postId}
            content={content}
            thumbnail={media.at(0)}
            counter={{ index: 1, total: media.length }}
          />
        );
      }
    },
    {
      accessorKey: 'engagement',
      header: t<string>('HeaderColumn.Engagement'),
      meta: { align: 'right', sortField: InstagramUserHashtagPostsSortOrder.ENGAGEMENT },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.engagement)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Like'),
      meta: { align: 'right', sortField: InstagramUserHashtagPostsSortOrder.LIKES },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comment'),
      meta: { align: 'right', sortField: InstagramUserHashtagPostsSortOrder.COMMENTS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { sortField: InstagramUserHashtagPostsSortOrder.POST_DATE },
      cell: ({ row }) => <TableCell.Date value={row.original.postDate} />
    }
  ];

  return { columns };
};
